import { useState } from 'react'
import {
  CertificateProgram,
  CertificateProgramEngagement,
  CertificateProgramEnrollment,
} from '@mc/api'
import { useExperience } from '@mc/experience'
import { useTranslation } from '@mc/i18n'
import { TRACK_CONTENT_TYPES } from '@mc/track-event'
import { useIsEnterpriseMember, useModalLock } from 'pages/certificates/common'
import {
  EmailCapture,
  EngagementActionBlock,
  EngagementNotice,
  EngagementPromo,
  LogoNotice,
  TrailerComingSoonPauseScreen,
  TrailerEnrolledPauseScreen,
  TrailerPauseScreen,
  VideoModal,
} from 'pages/certificates/components'
import { DownloadSyllabusModal } from 'pages/certificates/components/DownloadSyllabus'
import { HeroLayout } from 'pages/certificates/components/common/HeroLayout/HeroLayout'
import { Mccerts2279HeroTrailerLoopExp } from 'pages/certificates/experiments/mccerts-2279-trailer-thumbnail-autoplay'
import { PromoVariables } from 'pages/certificates/promo'
import { TrackingAttributes, useFireEvent } from 'pages/certificates/tracking'
import { CertHeadlineHero } from './CertHeadlineHero'

export interface HeroSectionProps {
  program: CertificateProgram
  comingSoon?: boolean
  available?: boolean
  engagement?: CertificateProgramEngagement
  enrollment?: CertificateProgramEnrollment
  certUuid: string
  promoVariables?: PromoVariables | null
  certSlug: string
  isSubscribed?: boolean
}

const TITLE_ID = 'certificate.title'

const getEngagementToShow = (
  subscriberInclusion: boolean,
  promo: boolean,
  emailCapture: boolean,
  notice: boolean,
  isEnterprise: boolean,
) => {
  if (subscriberInclusion) return { showSubscriptionInclusion: true }
  if (isEnterprise) return { showEngagement: true }
  if (promo) return { showPromo: true }
  if (emailCapture) return { showEmailCapture: true }
  if (notice) return { showNotice: true }
  return { showEngagement: true }
}

export const HeroSection = ({
  promoVariables,
  program,
  comingSoon = false,
  available = false,
  engagement,
  enrollment,
  certUuid,
  certSlug,
  isSubscribed = false,
}: HeroSectionProps) => {
  const { t } = useTranslation('@mc/certificates')
  const fireEvent = useFireEvent()
  const [showSyllabusModal, setShowSyllabusModal] = useState(false)
  const [showTrailerVideo, setShowTrailerVideo] = useState(false)
  const isEnterprise = useIsEnterpriseMember()
  const { isReady } = useExperience()
  const { acquireLock: acquireModalLock, releaseLock: releaseModalLock } =
    useModalLock()

  const { trailerMediaUuid } = program.videos || {}
  const logo = program.partner?.images?.logo

  const finalEngagement = engagement ?? program.defaultEngagement

  const {
    showSubscriptionInclusion,
    showPromo,
    showEmailCapture,
    showNotice,
    showEngagement,
  } = getEngagementToShow(
    isSubscribed && comingSoon, // subscribers only reach this page for a coming-soon cert (available ones auto-enroll + redirect)
    !!promoVariables?.cta,
    !!finalEngagement?.emailCapture,
    !!finalEngagement?.notice,
    isEnterprise,
  )

  const getPauseScreen = () => {
    const isUserEnrolled = enrollment?.isEnrolled
    const showComingSoon = comingSoon && isUserEnrolled
    const showEnrolled = !comingSoon && isUserEnrolled
    const handleVideoClose = () => {
      setShowTrailerVideo(false)
      releaseModalLock()
    }

    if (showComingSoon)
      return <TrailerComingSoonPauseScreen onClickBack={handleVideoClose} />

    if (showEnrolled)
      return <TrailerEnrolledPauseScreen onClickBack={handleVideoClose} />

    return (
      <TrailerPauseScreen
        subtext={program.certificateShowcase?.subtext}
        showCTA={!isEnterprise}
      />
    )
  }

  const onTrailerClick = () => {
    setShowTrailerVideo(true)
    acquireModalLock()
    fireEvent('certificateLandingClick', {
      action: t('marketingPages.common.playTrailer'),
    })
  }

  return (
    <>
      <section aria-labelledby={TITLE_ID}>
        <HeroLayout
          imageUrl={program.images?.hero1x1?.url}
          imageAlt={program.images?.hero1x1?.altText || ''}
          showTrailerButton={!!trailerMediaUuid}
          onTrailerClick={onTrailerClick}
          trailerMediaUuid={trailerMediaUuid}
          media={
            !!trailerMediaUuid && (
              <Mccerts2279HeroTrailerLoopExp
                mediaUuid={trailerMediaUuid}
                posterUrl={program.images?.hero1x1?.url}
                paused={showTrailerVideo}
                certSlug={certSlug}
              />
            )
          }
        >
          <CertHeadlineHero
            heroSection={program.heroSection}
            programTitle={program.title}
            programSubtitle={program.subtitle}
            companyLogo={logo}
          />

          {isReady && showSubscriptionInclusion && (
            <LogoNotice
              text={t('marketingPages.certificate.subscriptionInclusion')}
            />
          )}
          {isReady && showPromo && promoVariables?.cta && (
            <div>
              <EngagementPromo
                promo={promoVariables.promo}
                cta={promoVariables.cta}
                releaseLabel={program.releaseLabel}
                setShowSyllabusModal={setShowSyllabusModal}
                showSyllabusButton={!!program.syllabus?.pdfUrl}
                certUuid={certUuid}
                certSlug={certSlug}
                available={available}
                defaultEngagement={program.defaultEngagement?.actionBlock}
                engagement={engagement?.actionBlock}
              />
            </div>
          )}
          {isReady && showEmailCapture && finalEngagement && (
            <EmailCapture emailCapture={finalEngagement.emailCapture} />
          )}
          {isReady && showNotice && finalEngagement && (
            <EngagementNotice notice={finalEngagement.notice} />
          )}
          {isReady && showEngagement && (
            <EngagementActionBlock
              defaultEngagement={program.defaultEngagement?.actionBlock}
              engagement={engagement?.actionBlock}
              certUuid={certUuid}
              setShowSyllabusModal={setShowSyllabusModal}
            />
          )}
        </HeroLayout>
      </section>
      {program.syllabus?.pdfUrl && (
        <TrackingAttributes
          resource_link={program.syllabus.pdfUrl}
          resource_title={program.title}
        >
          <DownloadSyllabusModal
            show={showSyllabusModal}
            onClose={() => setShowSyllabusModal(false)}
            syllabusData={{
              syllabusPreviewImageUrl: program.syllabus.preview10x13?.url,
              title: program.title,
              syllabusPdfUrl: program.syllabus.pdfUrl,
            }}
          />
        </TrackingAttributes>
      )}
      {trailerMediaUuid && (
        <TrackingAttributes location='certificate trailer end screen'>
          <VideoModal
            show={showTrailerVideo}
            onCloseModal={() => {
              setShowTrailerVideo(false)
              releaseModalLock()
            }}
            mediaUuid={trailerMediaUuid}
            contentId={certUuid}
            contentType={TRACK_CONTENT_TYPES.CERTIFICATE_TRAILER}
            contentTitle={program.title ?? ''}
            pauseScreen={getPauseScreen()}
          />
        </TrackingAttributes>
      )}
    </>
  )
}
